import {useConfig} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {
  COLORS,
  HorizontalSlider,
  ImageType,
  ProductTileImage,
  ProductTileImageSliderSideButtons,
  SliderCounter,
  ThumbnailsSideButtons,
  useSlider,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {ReactNode, useRef} from 'react';

export interface ProductImageSliderProps {
  images: Array<ProductTileImageProps>;
  redirectPath?: string;
  currentOuterIndex?: number;
  slideCount?: number;
  columns?: number;
  showThumbnails?: boolean;
  BadgesListComponent?: ReactNode;
  WishlistButtonComponent?: ReactNode;
  replaceHistory?: boolean;
  previewImage?: ImageType;
}

interface ProductTileImageProps {
  src: string;
  alt?: string;
  padded?: boolean;
}

const THUMBNAILS_SLIDER_SLIDES_PER_PAGE = 4;

export const ProductTileImageSlider = ({
  images,
  redirectPath,
  showThumbnails = false,
  BadgesListComponent,
  WishlistButtonComponent,
  replaceHistory,
  previewImage,
}: ProductImageSliderProps) => {
  const [currentOuterIndex, setCurrentOuterIndex] = React.useState(0);
  const thumbnailsSliderRef = useRef<HTMLUListElement>(null);
  const {scrollToSlide} = useSlider(thumbnailsSliderRef);
  const {device} = useConfig();
  const {isActive: isOpenInNewTabActive} = useIsFeatureActive('ab_adp_new_tab');

  const mainSliderOnIndexChange = (currentIndex: number) => {
    setCurrentOuterIndex(currentIndex);
    scrollToSlide(currentIndex);
  };

  return (
    <Wrapper as="div">
      <SliderWishlistButtonWrapper>
        <HorizontalSlider
          dataTestId="ProductTileImageSlider"
          theme="light"
          slideCount={1}
          constrained
          disableHeaderControl
          currentOuterIndex={currentOuterIndex}
          onIndexChange={mainSliderOnIndexChange}
          navButtonsZIndex={1}
          addSlidesMarginToSliderWidth
          hideSideButtons
          SlideControlComponentBottom={
            images.length > 1 ? ProductTileImageSliderSideButtons : undefined
          }
        >
          {images.map((img: ProductTileImageProps, index: number) => {
            return (
              <Wrapper key={index} data-testid={`ProductTileImageSliderTile`}>
                <ProductTileImage
                  primaryImage={img}
                  breakpoints={MAIN_IMAGE_BREAKPOINTS}
                  importance={index === 0 ? 'high' : 'low'}
                  to={redirectPath}
                  openInNewTab={device === 'desktop' && isOpenInNewTabActive}
                  replaceHistory={replaceHistory}
                />
              </Wrapper>
            );
          })}
        </HorizontalSlider>
        {previewImage && (
          <ProductTileImagePreview
            primaryImage={{
              ...previewImage,
              src: previewImage.url,
            }}
            breakpoints={MAIN_IMAGE_BREAKPOINTS}
            importance="high"
          />
        )}
        {images.length > 1 && (
          <StyledSliderCounter
            currentSlide={currentOuterIndex + 1}
            slidesCount={images.length}
            dataTestid="productTileImageSlider_pagination"
          />
        )}
        {WishlistButtonComponent && (
          <WishlistButtonWrapper>
            {WishlistButtonComponent}
          </WishlistButtonWrapper>
        )}
        {BadgesListComponent && (
          <BadgesWrapper>{BadgesListComponent}</BadgesWrapper>
        )}
      </SliderWishlistButtonWrapper>

      {showThumbnails && (
        <StyledHorizontalSlider
          dataTestId="ProductTileImageSliderThumbnails"
          theme="dark"
          slideCount={THUMBNAILS_SLIDER_SLIDES_PER_PAGE}
          constrained
          disableHeaderControl
          addSlidesMarginToSliderWidth
          hideSideButtons
          SlideControlComponentBottom={ThumbnailsSideButtons}
          ref={thumbnailsSliderRef}
        >
          {images.map((img: ProductTileImageProps, index: number) => {
            return (
              <Wrapper
                data-testid={`ProductTileImageSliderThumbnailTile`}
                key={index}
              >
                <ThumbnailProductTileImage
                  onClick={e => {
                    e.preventDefault();
                    setCurrentOuterIndex(index);
                  }}
                  primaryImage={img}
                  breakpoints={THUMB_IMAGE_BREAKPOINTS}
                  $isActive={currentOuterIndex === index}
                />
              </Wrapper>
            );
          })}
        </StyledHorizontalSlider>
      )}
    </Wrapper>
  );
};

const MAIN_IMAGE_BREAKPOINTS = [{width: '295px'}];
const THUMB_IMAGE_BREAKPOINTS = [{width: '70px'}];

const Wrapper = styled.li`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const WishlistButtonWrapper = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  height: 24px;
  width: 24px;
  z-index: 1;
`;

const BadgesWrapper = styled.div`
  position: absolute;
  top: auto;
  bottom: 20px;
  left: 0;
  z-index: 1;
  width: 100%;
`;

const StyledHorizontalSlider = styled(HorizontalSlider)`
  margin-top: 5px;
  position: relative;
`;

const SliderWishlistButtonWrapper = styled.div`
  position: relative;
`;

const ProductTileImagePreview = styled(ProductTileImage)`
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-radius: 3px;
  z-index: 1;
`;

const ThumbnailProductTileImage = styled(ProductTileImage)<{
  $isActive: boolean;
}>`
  border: 1px solid
    ${({$isActive}) => ($isActive ? COLORS.Black : 'transparent')};
  border-radius: 4px;
  overflow: hidden;
`;

const StyledSliderCounter = styled(SliderCounter)`
  position: absolute;
  top: 20px;
  left: 20px;
`;
